import { Autocomplete, Box, MenuItem, TextField, Typography } from "@mui/material"
import type { AutocompleteProps } from "@mui/material/Autocomplete"
import type { AutocompleteRenderInputParams } from "@mui/material/Autocomplete/Autocomplete"
import { useTheme } from "@mui/material/styles"
import React, { useState } from "react"
import type { Control, FieldError } from "react-hook-form"
import { useController } from "react-hook-form"

import ArrowIcon from "../../../assets/icons/ArrowIcon"

import { FIELD_ERROR_CLASSES, FIELD_LABEL_CLASSES } from "@/constants/forms"

interface AutoCompleteProps<T = string>
  extends Omit<AutocompleteProps<T, true, true, false>, "renderInput" | "freeSolo" | "onChange" | "onInputChange"> {
  label: string
  placeholder: string
  name: string
  control: Control<any>
  defaultEmptyState?: T
  onInputChange?: (value: string) => void
  onChange?: (value: T) => void
  required?: boolean
  EndAdornment?: React.JSXElementConstructor<{ loading?: boolean; error?: FieldError }>
  listSelectionDisabled?: boolean
  noFirstValidate?: boolean
  renderInput?: (params: AutocompleteRenderInputParams) => React.ReactNode
  freeSolo?: boolean
  "data-testid"?: string
}

/**
 * @deprecated This component is deprecated. Use `AerosAutocomplete` from `@/components/UiKit/AerosAutocomplete` instead.
 * For React Hook Form integration, use `FormAutocomplete` from `@/components/UiKit/FormControls/FormAutocomplete`.
 */
const AutoComplete = <T,>({
  label,
  placeholder,
  id,
  onInputChange,
  onChange,
  options,
  loading,
  name,
  control,
  required,
  defaultEmptyState,
  freeSolo = false,
  listSelectionDisabled = false,
  noFirstValidate = false,
  getOptionLabel = (option) => {
    if (typeof option !== "string") {
      console.error("Please provide getOptionLabel function to convert object to string")
    }
    return option as string
  },
  EndAdornment,
  renderInput,
  "data-testid": testId,
  ...rest
}: AutoCompleteProps<T>): React.ReactNode => {
  const {
    field,
    fieldState: { error, isTouched },
  } = useController({
    name,
    control,
  })
  const theme = useTheme()

  // Workaround to avoid changing default value
  // described here: https://github.com/mui/material-ui/issues/36467
  const [defaultValue] = useState(field.value)
  const showEndAdornment = noFirstValidate ? !!EndAdornment && isTouched : !!EndAdornment

  const handleInputChange = (value: string) => {
    if (freeSolo) {
      field.onChange(value)
    }
    // Resets field value to default state if the input is empty
    // Needed as field may have T type and not string
    else if (!value) {
      field.onChange(defaultEmptyState)
    }
    onInputChange?.(value)
  }

  const handleAutocompleteChange = (value: T | string) => {
    // Resets field value to default state use wipes the field
    // otherwise sets the field value to null
    if (!freeSolo) {
      const resolvedValue = value ?? defaultEmptyState
      field.onChange(resolvedValue)
      onChange?.(resolvedValue as T)
    }
  }

  return (
    <Box className='mb-4' position='relative' data-testid={testId}>
      {label && (
        <label className={FIELD_LABEL_CLASSES}>
          {label}
          {required && <span className='text-red-600'>*</span>}
        </label>
      )}
      <Autocomplete
        {...field}
        id={id}
        onInputChange={(_event, newInputValue) => handleInputChange(newInputValue)}
        onChange={(_event, newAutocompleteValue) => handleAutocompleteChange(newAutocompleteValue as T)}
        defaultValue={defaultValue}
        options={options}
        getOptionLabel={getOptionLabel}
        classes={{
          input: "p-0 border-0 ring-0 outline-0 focus:border-0 focus:ring-0 focus:outline-none text-sm",
        }}
        loading={loading}
        renderInput={(params) => (
          <TextField
            {...params}
            required={required}
            placeholder={placeholder}
            fullWidth
            variant='outlined'
            error={!!error && field?.value?.length > 0}
            slotProps={{
              input: {
                ...params.InputProps,
                ...(showEndAdornment &&
                  EndAdornment && {
                    endAdornment: <EndAdornment loading={loading} error={error} />,
                  }),
              },
            }}
            sx={{
              "& .MuiInputBase-root": {
                backgroundColor: rest?.disabled ? theme.palette.grey[200] : "inherit",
                paddingLeft: "1rem",
              },
              "& .MuiInputBase-input": {
                backgroundColor: rest?.disabled ? theme.palette.grey[200] : "inherit",
              },
              "& .MuiOutlinedInput-root.Mui-disabled .MuiOutlinedInput-notchedOutline": {
                borderColor: theme.palette.divider, // Reset to default border color
              },
            }}
          />
        )}
        renderOption={(props, option, state) => (
          <MenuItem
            {...props}
            key={(typeof option === "string" ? option : (getOptionLabel?.(option) ?? "")) + state.index}
            disabled={listSelectionDisabled}
          >
            {getOptionLabel(option)}
          </MenuItem>
        )}
        popupIcon={<ArrowIcon size={"14px"} extraStyles={{ marginRight: "3px", position: "relative", top: "-3px" }} />}
        sx={{
          "& .MuiAutocomplete-popupIndicator:hover": {
            background: "transparent",
          },
        }}
        {...rest}
      />
      {error && <Typography className={FIELD_ERROR_CLASSES}>{error.message}</Typography>}
    </Box>
  )
}

export default AutoComplete
